import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  locationService,
  zoneService,
  binService,
  storageUnitService,
  carrierService,
} from "/app/src/services";
import { useTranslation } from "react-i18next";
import {
  Bin,
  Location as LocationType,
  StorageUnit,
  Zone as ZoneType,
} from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";

/**
 * Renders a single location view including details and a list of location contents.
 *
 * @returns {JSX.Element} The rendered React component.
 */
export default function SingleLocation() {
  const { t } = useTranslation();
  const { locationId } = useParams();
  const handleNavigationError = useHandleNavigationError();

  //Get the Single Location from URL
  const { data: location } = useQuery({
    queryKey: ["location", locationId],
    queryFn: () => {
      return locationService.getSingle(locationId).catch(() => {
        handleNavigationError();
      });
    },
    enabled: Boolean(locationId),
    retry: false,
    initialData: { location: {} },
    select: (data: { location: LocationType }) => {
      return data.location;
    },
  });

  const { data: bin } = useQuery({
    queryKey: ["bin", location.binId],
    queryFn: () => {
      return binService.getSingle(location.binId);
    },
    enabled: Boolean(location.binId),
    initialData: { bin: {} },
    select: (data: { bin: Bin }) => {
      return data.bin;
    },
  });

  const { data: zone } = useQuery({
    queryKey: ["zone", location.zoneId],
    queryFn: () => {
      return zoneService.getSingle(location.zoneId);
    },
    enabled: Boolean(location.zoneId),
    initialData: { zone: {} },
    select: (data: { zone: ZoneType }) => {
      return data.zone;
    },
  });

  /**
   * Get localized orientation label for a numeric orientation code.
   *
   * @param orientation - Orientation code (1 = no rotation, 2 = 90°, 3 = 180°, 4 = 270°)
   * @returns Localized orientation description.
   */
  const getOrientationText = (orientation: number): string => {
    switch (orientation) {
      case 1:
        return t("translation:no_rotation");
      case 2:
        return t("translation:90_degrees");
      case 3:
        return t("translation:180_degrees");
      case 4:
        return t("translation:270_degrees");
      default:
        return t("translation:no_rotation");
    }
  };

  const { data: storageUnit } = useQuery({
    queryKey: ["storageUnit", location.storageUnitId],
    queryFn: () => {
      return storageUnitService.getSingle(location.storageUnitId);
    },
    enabled: Boolean(location.storageUnitId),
    initialData: { storage_unit: {} },
    select: (data: { storage_unit: StorageUnit }) => {
      return data.storage_unit;
    },
  });

  const { data: carrier } = useQuery({
    queryKey: ["carrier", location.carrierId],
    queryFn: () => {
      return carrierService.getSingle(location.carrierId);
    },
    enabled: Boolean(location.carrierId),
    initialData: { carrier: {} },
    select: (data: { carrier }) => {
      return data.carrier;
    },
  });

  const basicInfo = [
    { label: t("translation:name"), value: location?.name },
    {
      label: t("translation:type_description"),
      value: location?.typeDescription,
    },
    { label: t("translation:warehouse"), value: location?.warehouseName },
    { label: t("translation:storage_unit"), value: storageUnit?.name },
    {
      label: t("translation:carrier"),
      value: carrier?.name,
    },
  ];

  const capacityInfo = [
    {
      label: t("translation:current_quantity"),
      value: location?.currentQuantity,
    },
    { label: t("translation:capacity_used"), value: location?.capacityUsed },
    {
      label: t("translation:capacity_reserved"),
      value: location?.capacityReserved,
    },
    {
      label: t("translation:marked_full"),
      value: location?.isMarkedFull
        ? t("translation:yes")
        : t("translation:no"),
    },
  ];

  const locationProperties = [
    {
      label: t("translation:multi_occupancy"),
      value:
        location?.isMultiOccupancy === 1
          ? t("translation:yes")
          : t("translation:no"),
    },
    {
      label: t("translation:orientation"),
      value: getOrientationText(location?.orientation),
    },
    { label: t("translation:width"), value: location?.width },
    { label: t("translation:depth"), value: location?.depth },
  ];

  const relatedModels = [
    {
      label: t("translation:bin"),
      value: bin?.name,
      link: `/explorer/bins/${bin?.id}`,
    },
    {
      label: t("translation:zone"),
      value: zone?.name,
      link: `/explorer/zones/${zone?.id}`,
    },
  ];

  return (
    <div className="explorer">
      <Helmet>
        <title>{location.name}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: "Locations", to: "/explorer/locations" },
          { label: location?.name },
        ]}
      />
      <h1 className="text-[36px] font-medium">{location?.name}</h1>
      <h2 className="text-[16px] mb-6 text-neutral-smoke">
        Location Id: {location?.id}
      </h2>
      <InfoList header={t("translation:basic_info")} items={basicInfo} />
      <InfoList header={t("translation:capacity_info")} items={capacityInfo} />
      <InfoList
        header={t("translation:location_properties")}
        items={locationProperties}
      />
      <InfoList
        header={t("translation:related_models")}
        items={relatedModels}
      />
    </div>
  );
}
